import { Skeleton } from 'antd';
import { setSignupContextAction } from 'common/commonRedux/auth/actions';
import { useAxiosRequest } from 'common/hooks';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

type BillingPeriod = 'Monthly' | 'Yearly';

interface PlanPricing {
  billingPeriod: BillingPeriod;
  pricePerUser: number;
  discountPercent: number;
  effectivePricePerUser: number;
  currencyCode: string;
  currencySymbol: string;
  isDiscountActive: boolean;
  discountValidFrom: string | null;
  discountValidTo: string | null;
  isDiscountForever: boolean;
}

interface Plan {
  planId: number;
  planName: string;
  description: string;
  planType: string;
  isSelfServe: boolean;
  sortOrder: number;
  pricing: PlanPricing[];
  features: string[];
}

interface PublicPlansResponse {
  trialDays: number;
  trialEmployeeSlots: number;
  trialEmployeeSlotsLabel: string;
  trialPlanType: string;
  trialPlanName: string;
  plans: Plan[];
}

const formatPrice = (value: number) => value.toFixed(2);

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    className="flex-shrink-0"
  >
    <path
      d="M10 0C4.5 0 0 4.5 0 10C0 15.5 4.5 20 10 20C15.5 20 20 15.5 20 10C20 4.5 15.5 0 10 0ZM14.3 7.6L9.7 13.6C9.5 13.8 9.2 14 8.9 14C8.6 14 8.3 13.9 8.1 13.6L5.7 10.5C5.4 10.1 5.4 9.4 5.9 9.1C6.4 8.8 7 8.8 7.3 9.3L8.9 11.4L12.7 6.4C13 6 13.7 5.9 14.1 6.2C14.6 6.5 14.6 7.1 14.3 7.6Z"
      fill="#02BC7D"
    />
  </svg>
);

const SparkleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l1.7 5.3L19 9l-5.3 1.7L12 16l-1.7-5.3L5 9l5.3-1.7L12 2zm7 11l.85 2.65L22.5 16.5l-2.65.85L19 20l-.85-2.65L15.5 16.5l2.65-.85L19 13zM5 14l.85 2.65L8.5 17.5l-2.65.85L5 21l-.85-2.65L1.5 17.5l2.65-.85L5 14z" />
  </svg>
);

const SubscriptionPlan: FC = () => {
  const { packageId } = useParams();
  const [searchParams] = useSearchParams();
  const billingFromUrl = searchParams.get('billing') as BillingPeriod | null;
  const trialFromUrl = searchParams.get('trial') === 'true';
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const trialActive: boolean = useSelector(
    (state: any) => !!state.auth?.profileData?.signupContext?.isTrial
  );
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(
    billingFromUrl === 'Monthly' || billingFromUrl === 'Yearly'
      ? billingFromUrl
      : 'Monthly'
  );
  const [activePlanId, setActivePlanId] = useState<number | null>(null);
  const [slideDir, setSlideDir] = useState<'right' | 'left'>('right');
  const [phase, setPhase] = useState<'idle' | 'exit' | 'enter'>('idle');
  const [pulseId, setPulseId] = useState<number | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    return () => timers.current.forEach((t) => clearTimeout(t));
  }, []);

  // Clear persisted trial state from Redux when landing without ?trial=true
  useEffect(() => {
    if (!trialFromUrl) {
      dispatch(setSignupContextAction({ isTrial: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const STAGGER_MS = 80;
  const EXIT_MS = 420;
  const ENTER_MS = 560;
  const PULSE_MS = 700;
  const CARD_COUNT = 3;

  const changeBilling = (bp: BillingPeriod) => {
    if (bp === billingPeriod || phase !== 'idle') return;
    const dir: 'right' | 'left' = bp === 'Yearly' ? 'right' : 'left';
    setSlideDir(dir);
    setPhase('exit');
    const exitTotal = EXIT_MS + (CARD_COUNT - 1) * STAGGER_MS;
    const enterTotal = ENTER_MS + (CARD_COUNT - 1) * STAGGER_MS;
    const t1 = setTimeout(() => {
      setBillingPeriod(bp);
      setPhase('enter');
      // When trial is active, preserve trial state and URL — billing period is only
      // used to pre-select prices if the user later picks a paid plan.
      if (!trialActive) {
        navigate(`${window.location.pathname}?billing=${bp}`, {
          replace: true,
        });
        // Keep signup context in sync when user changes billing period on an already-selected plan
        if (activePlanId !== null) {
          const activePlan = sortedPlans.find((p) => p.planId === activePlanId);
          if (activePlan?.isSelfServe) {
            const pricing =
              activePlan.pricing?.find((p) => p.billingPeriod === bp) ||
              activePlan.pricing?.[0];
            dispatch(
              setSignupContextAction({
                isTrial: false,
                billingPeriod: bp,
                planId: activePlan.planId,
                planType: activePlan.planType,
                planName: activePlan.planName,
                effectivePricePerUser: pricing?.effectivePricePerUser,
                currencySymbol: pricing?.currencySymbol,
              })
            );
          }
        }
      }
      const t2 = setTimeout(() => setPhase('idle'), enterTotal);
      timers.current.push(t2);
    }, exitTotal);
    timers.current.push(t1);
  };

  const triggerPulse = (planId: number) => {
    setPulseId(planId);
    const t = setTimeout(() => setPulseId(null), PULSE_MS);
    timers.current.push(t);
  };

  const plansReq = useAxiosRequest('');

  // Track whether the layout is in the 3-column form (md+) so slot swapping only kicks in there.
  const [isWide, setIsWide] = useState<boolean>(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(min-width: 768px)').matches
      : true
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsWide(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // The exit/enter slide covers 80px of horizontal travel, tuned for the
  // 3-column desktop layout — on a single stacked mobile column that reads
  // as a jarring sideways jerk, so skip it there and swap plans in place.
  const wrapperAnimClass = (() => {
    if (!isWide) return '';
    if (phase === 'exit') {
      return slideDir === 'right'
        ? 'animate-card-exit-right'
        : 'animate-card-exit-left';
    }
    if (phase === 'enter') {
      return slideDir === 'right'
        ? 'animate-card-enter-from-left'
        : 'animate-card-enter-from-right';
    }
    return '';
  })();

  useEffect(() => {
    plansReq.apiAction({ urlObjKey: 'GetPublicPricePlans', method: 'GET' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data: PublicPlansResponse | null =
    plansReq.resData && typeof plansReq.resData === 'object'
      ? (plansReq.resData as PublicPlansResponse)
      : null;

  const sortedPlans = useMemo<Plan[]>(() => {
    if (!data?.plans?.length) return [];
    return [...data.plans].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [data]);

  // Mobile: cards are a horizontal swipe carousel (one at a time) instead of
  // a stacked column, so picking a plan doesn't mean scrolling past 3 full-height cards.
  const MOBILE_CARD_WIDTH = 250;
  const MOBILE_CARD_GAP = 16;
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [carouselIdx, setCarouselIdx] = useState(0);
  // Side padding so the FIRST and LAST card can also reach true center —
  // without it, scroll-snap clamps at the scroll bounds and those two cards
  // land flush against the edge instead of centered.
  const [carouselSidePad, setCarouselSidePad] = useState(16);

  useEffect(() => {
    if (isWide) return;
    const el = carouselRef.current;
    if (!el) return;
    const measure = () => {
      setCarouselSidePad(
        Math.max((el.clientWidth - MOBILE_CARD_WIDTH) / 2, 12)
      );
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isWide]);

  const scrollToCard = (idx: number) => {
    cardRefs.current[idx]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  };

  const carouselSettleTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  useEffect(() => {
    return () => {
      if (carouselSettleTimeout.current) {
        clearTimeout(carouselSettleTimeout.current);
      }
    };
  }, []);

  const handleCarouselScroll = () => {
    const el = carouselRef.current;
    if (!el || isWide) return;
    const cardStep = MOBILE_CARD_WIDTH + MOBILE_CARD_GAP;
    const idx = Math.min(
      Math.max(Math.round(el.scrollLeft / cardStep), 0),
      sortedPlans.length - 1
    );
    setCarouselIdx(idx);

    // Whichever card the swipe settles on becomes the selected plan —
    // debounced so it fires once the scroll actually stops, not on every tick.
    if (carouselSettleTimeout.current) {
      clearTimeout(carouselSettleTimeout.current);
    }
    carouselSettleTimeout.current = setTimeout(() => {
      const plan = sortedPlans[idx];
      if (plan && plan.planId !== activePlanId) {
        handleSelect(plan);
      }
    }, 180);
  };

  // Bring the just-selected plan into view when browsing the mobile carousel.
  useEffect(() => {
    if (isWide || activePlanId === null) return;
    const idx = sortedPlans.findIndex((p) => p.planId === activePlanId);
    if (idx === -1) return;
    const t = setTimeout(() => scrollToCard(idx), 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlanId, isWide]);

  const trialPlan = useMemo<Plan | null>(() => {
    if (!data?.trialPlanType) return null;
    return sortedPlans.find((p) => p.planType === data.trialPlanType) || null;
  }, [sortedPlans, data]);

  const canStartTrial = !!data && !!trialPlan && (data.trialDays ?? 0) > 0;

  // Default highlight: an explicit /:packageId link wins; otherwise a bare
  // /signup defaults to the trial (not a paid plan) — falling back to
  // Professional → first self-serve → first plan only if trial isn't offered.
  // Also seeds Redux signupContext so downstream steps have the correct plan data
  useEffect(() => {
    if (!sortedPlans.length || activePlanId !== null) return;
    const fromUrl = packageId
      ? sortedPlans.find((p) => String(p.planId) === packageId)
      : null;

    let defaultPlan: Plan | undefined;
    let isTrialSelection = false;

    if (fromUrl) {
      defaultPlan = fromUrl;
      isTrialSelection =
        trialFromUrl && !!trialPlan && fromUrl.planId === trialPlan.planId;
    } else if (canStartTrial && trialPlan) {
      defaultPlan = trialPlan;
      isTrialSelection = true;
    } else {
      const professionalPlan = sortedPlans.find(
        (p) =>
          p.planType?.toLowerCase().includes('professional') ||
          p.planName?.toLowerCase().includes('professional')
      );
      const firstSelfServe = sortedPlans.find((p) => p.isSelfServe);
      defaultPlan = professionalPlan || firstSelfServe || sortedPlans[0];
    }
    if (!defaultPlan) return;
    setActivePlanId(defaultPlan.planId);

    if (defaultPlan.isSelfServe) {
      const pricing =
        defaultPlan.pricing?.find((p) => p.billingPeriod === billingPeriod) ||
        defaultPlan.pricing?.[0];
      dispatch(
        setSignupContextAction({
          isTrial: isTrialSelection,
          billingPeriod,
          planId: defaultPlan.planId,
          planType: defaultPlan.planType,
          planName: defaultPlan.planName,
          ...(isTrialSelection
            ? {
                trialDays: data?.trialDays,
                trialEmployeeSlots: data?.trialEmployeeSlots,
                trialEmployeeSlotsLabel: data?.trialEmployeeSlotsLabel,
              }
            : {
                effectivePricePerUser: pricing?.effectivePricePerUser,
                currencySymbol: pricing?.currencySymbol,
              }),
        })
      );
    }
  }, [
    sortedPlans,
    packageId,
    activePlanId,
    trialFromUrl,
    trialPlan,
    canStartTrial,
    billingPeriod,
    dispatch,
    data,
  ]);

  // Auto-activate trial when URL has ?trial=true
  useEffect(() => {
    if (!trialFromUrl || !trialPlan || !data || trialActive) return;
    dispatch(
      setSignupContextAction({
        isTrial: true,
        billingPeriod,
        planId: trialPlan.planId,
        planType: trialPlan.planType,
        planName: trialPlan.planName,
        trialDays: data.trialDays,
        trialEmployeeSlots: data.trialEmployeeSlots,
        trialEmployeeSlotsLabel: data.trialEmployeeSlotsLabel,
      })
    );
  }, [trialFromUrl, trialPlan, data]);

  // Find current active position (used to tilt non-active cards toward the selected one).
  const activeIndex = useMemo<number>(() => {
    if (activePlanId === null) return -1;
    return sortedPlans.findIndex((p) => p.planId === activePlanId);
  }, [sortedPlans, activePlanId]);

  const getPricing = (plan: Plan): PlanPricing | undefined =>
    plan.pricing?.find((p) => p.billingPeriod === billingPeriod) ||
    plan.pricing?.[0];

  const handleSelect = (plan: Plan) => {
    if (plan.planId !== activePlanId) {
      triggerPulse(plan.planId);
    }
    setActivePlanId(plan.planId);
    // Custom plan: just mark it selected (bring forward). Redirect happens only
    // when the user explicitly clicks the "Contact to Sales" button below.
    if (!plan.isSelfServe) {
      return;
    }
    const pricing = getPricing(plan);
    dispatch(
      setSignupContextAction({
        isTrial: false,
        billingPeriod,
        planId: plan.planId,
        planType: plan.planType,
        planName: plan.planName,
        effectivePricePerUser: pricing?.effectivePricePerUser,
        currencySymbol: pricing?.currencySymbol,
      })
    );
    navigate(
      `/${plan.planId}/${plan.planType}/signup?billing=${billingPeriod}`,
      { replace: true }
    );
  };

  const handleContactSales = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open('https://trackforce.io/contact/', '_blank');
  };

  const handleStartTrial = () => {
    if (!trialPlan || !data) return;
    dispatch(
      setSignupContextAction({
        isTrial: true,
        billingPeriod,
        planId: trialPlan.planId,
        planType: trialPlan.planType,
        planName: trialPlan.planName,
        trialDays: data.trialDays,
        trialEmployeeSlots: data.trialEmployeeSlots,
        trialEmployeeSlotsLabel: data.trialEmployeeSlotsLabel,
      })
    );
    navigate(`/${trialPlan.planId}/${trialPlan.planType}/signup?trial=true`, {
      replace: true,
    });
  };

  const isLoading = plansReq.isLoading && !data;

  return (
    <div className="px-4 flex flex-col items-center justify-center font-sans py-8 h-full">
      <style>
        {`@keyframes trialGlassShimmer {
          0% { transform: translateX(-130%); }
          100% { transform: translateX(130%); }
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}
      </style>
      {/* Trial Banner */}
      {!isLoading && canStartTrial && trialPlan && data && (
        <div
          className={`relative z-10 max-w-6xl w-full mb-6 transition-opacity duration-300 ${!trialActive && activePlanId !== null ? 'opacity-90' : 'opacity-100'}`}
        >
          {/* The whole banner is the click target, matching the paid plan cards below —
              previously only the button inside it responded, so clicking the card body
              did nothing. Already-selected is a no-op. */}
          <div
            role="button"
            tabIndex={trialActive ? -1 : 0}
            aria-pressed={trialActive}
            onClick={() => {
              if (!trialActive) handleStartTrial();
            }}
            onKeyDown={(e) => {
              if (trialActive) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleStartTrial();
              }
            }}
            className={[
              'group relative overflow-hidden rounded-2xl shadow-2xl transition-all duration-300',
              trialActive ? 'cursor-default' : 'cursor-pointer',
              trialActive
                ? 'bg-gradient-to-r from-blue-500 via-blue-300 to-blue-100 ring-4 ring-primaryColor/60 ring-offset-2 ring-offset-[#0f172a]'
                : 'bg-gradient-to-r from-primaryColor via-blue-300 to-blue-100',
            ].join(' ')}
          >
            {/* decorative shimmer */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(255,255,255,0.25),transparent_50%)]" />
            {trialActive && (
              <span
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.6) 50%, transparent 70%)',
                  animation: 'trialGlassShimmer 1.8s linear infinite',
                }}
              />
            )}
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-3 md:gap-4 px-4 md:px-6 py-4 md:py-5">
              <div className="flex items-center gap-3 md:gap-4 text-white text-center md:text-left">
                <div
                  className={`hidden md:flex w-12 h-12 rounded-full backdrop-blur items-center justify-center text-white flex-shrink-0 transition-all ${
                    trialActive ? 'bg-white/30 scale-110' : 'bg-white/20'
                  }`}
                >
                  {trialActive ? (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
                    </svg>
                  ) : (
                    <SparkleIcon />
                  )}
                </div>
                <div>
                  {trialActive ? (
                    <>
                      <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest uppercase bg-white text-emerald-700 px-2 py-0.5 rounded-full mb-1">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                        Free Trial Selected
                      </div>
                      <div className="text-lg md:text-xl font-bold leading-tight">
                        {data.trialDays}-day trial of {data.trialPlanName}
                      </div>
                      <div className="text-sm opacity-95 mt-0.5 font-medium">
                        Enter your email on the left to continue →
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-xs font-bold tracking-widest uppercase opacity-90 mb-0.5">
                        No credit card required
                      </div>
                      <div className="text-lg md:text-xl font-bold leading-tight">
                        Try {data.trialPlanName} free for {data.trialDays} days
                      </div>
                      <div className="text-sm opacity-90 mt-0.5">
                        {data.trialEmployeeSlotsLabel} employees · Full feature
                        access · Cancel anytime
                      </div>
                    </>
                  )}
                </div>
              </div>
              <button
                type="button"
                // stopPropagation so the click isn't also handled by the banner wrapper.
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartTrial();
                }}
                disabled={trialActive}
                className={`relative overflow-hidden font-bold rounded-full px-6 py-3 shadow-lg whitespace-nowrap transition-all flex items-center gap-2 ${
                  trialActive
                    ? 'bg-white/95 text-emerald-700 cursor-default'
                    : 'bg-white text-blue-700 hover:bg-blue-50 hover:scale-105 active:scale-95'
                }`}
              >
                {!trialActive && (
                  <span
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background:
                        'linear-gradient(110deg, transparent 30%, rgba(37,99,235,0.10) 50%, transparent 70%)',
                      animation: 'trialGlassShimmer 1.8s linear infinite',
                    }}
                  />
                )}
                {trialActive ? (
                  <>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
                    </svg>
                    Trial Selected
                  </>
                ) : (
                  <>
                    Start Free Trial
                    <span className="transition-transform group-hover:translate-x-1">
                      →
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header: billing toggle */}
      {data && (
        <div className="relative z-10 max-w-6xl w-full flex flex-col md:flex-row items-center justify-between gap-3 mb-6 text-white">
          <p className="text-sm opacity-90 hidden md:block">
            {trialActive
              ? 'Or skip the trial — pick a plan to pay now:'
              : 'Already know what you need? Pick a plan below.'}
          </p>
          <div className="inline-flex bg-white/15 backdrop-blur rounded-full p-1 border border-white/20">
            {(['Monthly', 'Yearly'] as BillingPeriod[]).map((bp) => (
              <button
                key={bp}
                type="button"
                onClick={() => changeBilling(bp)}
                disabled={phase !== 'idle' && bp !== billingPeriod}
                className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-all ${
                  billingPeriod === bp
                    ? 'bg-white text-blue-700 shadow'
                    : 'text-white/90 hover:text-white'
                }`}
              >
                {bp}
                {bp === 'Yearly' && (
                  <span className="ml-1 text-[10px] font-bold text-emerald-600">
                    SAVE
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="relative z-10 max-w-6xl w-full grid grid-cols-1 md:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-white rounded-2xl p-8 shadow-xl">
              <Skeleton active paragraph={{ rows: 8 }} />
            </div>
          ))}
        </div>
      )}

      {/* Pricing Table Grid — selection uses depth swap (Z) + circular Y-tilt */}
      {!isLoading && sortedPlans.length > 0 && (
        <>
        <div
          ref={carouselRef}
          onScroll={handleCarouselScroll}
          className={`no-scrollbar relative z-10 max-w-6xl w-full flex overflow-x-auto snap-x snap-mandatory gap-4 pt-12 -mt-6 pb-1 md:grid md:grid-cols-3 md:gap-0 md:!px-0 md:pt-0 md:mt-0 md:overflow-visible md:snap-none items-stretch transition-all duration-300 ${
            trialActive ? 'opacity-50' : 'opacity-100'
          }`}
          style={{
            perspective: '1450px',
            perspectiveOrigin: 'center 40%',
            ...(!isWide
              ? { paddingLeft: carouselSidePad, paddingRight: carouselSidePad }
              : {}),
          }}
        >
          {sortedPlans.map((plan, idx) => {
            const isActive = activePlanId === plan.planId;
            const isPopular = plan.planType === data?.trialPlanType;
            const pricing = getPricing(plan);
            const isCustom = !plan.isSelfServe;
            const isPulsing = pulseId === plan.planId;

            // Active card → center slot. The card occupying center moves to active's old slot.
            // Other cards stay where they are. Only meaningful for 3-card layout.
            const hasActive = activeIndex >= 0;
            const isThreeCards = sortedPlans.length === 3;
            const middleIdx = 1;
            let visualIdx = idx;
            if (isThreeCards && hasActive && activeIndex !== middleIdx) {
              if (isActive) visualIdx = middleIdx;
              else if (idx === middleIdx) visualIdx = activeIndex;
            }
            const slotShift = visualIdx - idx; // -1, 0, or +1
            const visualOffset = visualIdx - middleIdx; // for tilt direction (desktop, post slot-swap)
            // Mobile never reorders (translateX stays 0), so tilt direction there
            // has to read straight off the card's real position vs. the active one.
            const mobileOffset = idx - activeIndex;

            const rotateY =
              isActive || !hasActive
                ? 0
                : (isWide ? visualOffset : mobileOffset) < 0
                  ? 14
                  : (isWide ? visualOffset : mobileOffset) > 0
                    ? -14
                    : 0;
            const translateZ = isActive ? 40 : hasActive ? -30 : 0;
            const translateY = isActive ? -8 : 0;
            const scale = isActive ? 1.05 : hasActive ? 0.94 : 1;
            const opacity = isActive || !hasActive ? 1 : 0.75;
            const translateX =
              isWide && slotShift !== 0 ? `${slotShift * 100}%` : '0%';

            return (
              <div
                key={`card-${plan.planId}-${billingPeriod}-${phase}`}
                ref={(el) => {
                  cardRefs.current[idx] = el;
                }}
                className={`${wrapperAnimClass} flex pointer-events-none shrink-0 snap-center md:w-auto md:shrink`}
                style={{
                  animationDelay: wrapperAnimClass
                    ? `${idx * STAGGER_MS}ms`
                    : undefined,
                  perspective: '1500px',
                  ...(!isWide ? { width: MOBILE_CARD_WIDTH } : {}),
                }}
              >
                <div
                  onClick={() => handleSelect(plan)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleSelect(plan);
                  }}
                  style={{
                    transform: `translateX(${translateX}) translateZ(${translateZ}px) translateY(${translateY}px) scale(${scale}) rotateY(${rotateY}deg)`,
                    opacity,
                    zIndex: isActive ? 20 : 1,
                    transformStyle: 'preserve-3d',
                    pointerEvents: 'auto',
                    transition:
                      'transform 800ms cubic-bezier(0.34, 1.3, 0.6, 1), opacity 500ms ease, box-shadow 500ms ease, border-color 400ms ease',
                    backfaceVisibility: 'hidden',
                  }}
                  className={[
                    'relative flex flex-col bg-white rounded-2xl border p-5 md:p-7 cursor-pointer w-full',
                    'h-[440px] md:h-[600px]',
                    'will-change-transform',
                    isActive
                      ? 'shadow-2xl border-blue-300 ring-2 ring-blue-300'
                      : 'shadow-xl border-gray-100 hover:shadow-2xl',
                    isPulsing ? 'animate-select-pulse' : '',
                  ].join(' ')}
                >
                  {(isActive || isPopular) && (
                    <div
                      className={`overflow-hidden absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 py-1.5 rounded-full text-[10px] font-bold tracking-wider shadow-lg whitespace-nowrap ${
                        isActive
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-500 text-white'
                          : 'bg-blue-50 text-blue-700 border border-blue-200'
                      }`}
                    >
                      {isActive && (
                        <span
                          className="pointer-events-none absolute inset-0"
                          style={{
                            background:
                              'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.6) 50%, transparent 70%)',
                            animation: 'trialGlassShimmer 1.8s linear infinite',
                          }}
                        />
                      )}
                      {isActive
                        ? isPopular
                          ? 'Most popular'
                          : 'Selected'
                        : 'Most popular'}
                    </div>
                  )}

                  <div className="text-center mb-4 mt-1">
                    <h2 className="text-xl font-bold text-gray-800 tracking-tight">
                      {plan.planName}
                    </h2>
                    <p className="text-gray-500 text-xs mt-1 font-medium leading-snug min-h-[32px]">
                      {plan.description}
                    </p>
                  </div>

                  <div className="text-center mb-5 min-h-[72px] flex flex-col justify-center">
                    {isCustom ? (
                      <span className="text-gray-400 italic text-sm">
                        Contact for pricing
                      </span>
                    ) : pricing ? (
                      <>
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold text-gray-800">
                            {pricing.currencySymbol}
                            {formatPrice(pricing.effectivePricePerUser)}
                          </span>
                          <span className="text-gray-500 text-xs font-medium">
                            / user / mo
                          </span>
                        </div>
                        <div className="mt-1 flex flex-col items-center justify-center gap-1 min-h-[20px]">
                          {pricing.isDiscountActive &&
                          pricing.discountPercent > 0 ? (
                            <>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400 line-through">
                                  {pricing.currencySymbol}
                                  {formatPrice(pricing.pricePerUser)}
                                </span>
                                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                                  Save {pricing.discountPercent}%
                                </span>
                              </div>
                              {pricing.isDiscountForever ? (
                                <span className="text-[10px] font-medium text-emerald-600">
                                  {pricing.discountValidTo
                                    ? `Locked-in — enroll by ${new Date(pricing.discountValidTo).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}`
                                    : 'Locked-in rate — yours forever'}
                                </span>
                              ) : pricing.discountValidTo ? (
                                <span className="text-[10px] font-medium text-amber-600">
                                  Ends{' '}
                                  {new Date(
                                    pricing.discountValidTo
                                  ).toLocaleDateString(undefined, {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                  })}
                                </span>
                              ) : null}
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">
                              Billed{' '}
                              {billingPeriod === 'Yearly'
                                ? 'yearly'
                                : 'monthly'}
                            </span>
                          )}
                        </div>
                      </>
                    ) : null}
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1 mb-5">
                    <ul className="space-y-2.5">
                      {(plan.features?.length
                        ? plan.features
                        : isCustom
                          ? [
                              'Everything in Essential or Professional',
                              'Plan customization',
                              'Custom feature development',
                              'Dedicated support',
                            ]
                          : []
                      ).map((feature) => (
                        <li
                          key={feature}
                          className="flex items-start gap-2 text-sm text-gray-700"
                        >
                          <span className="mt-0.5">
                            <CheckIcon />
                          </span>
                          <span className="font-medium leading-snug">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isCustom) {
                        handleContactSales(e);
                      } else {
                        handleSelect(plan);
                      }
                    }}
                    className={`relative overflow-hidden w-full py-2 md:py-3 px-6 rounded-xl font-semibold transition-all flex justify-center items-center gap-2 ${
                      isCustom
                        ? 'bg-[#212121] text-white hover:bg-black'
                        : isActive
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {!isCustom && isActive && (
                      <span
                        className="pointer-events-none absolute inset-0"
                        style={{
                          background:
                            'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.6) 50%, transparent 70%)',
                          animation: 'trialGlassShimmer 1.8s linear infinite',
                        }}
                      />
                    )}
                    {isCustom
                      ? 'Contact to Sales'
                      : isActive && !trialActive
                        ? 'Selected'
                        : 'Choose plan'}
                    {isCustom && (
                      <span className="transition-transform group-hover:translate-x-1">
                        →
                      </span>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Swipe-position dots — mobile carousel only */}
        <div className="flex md:hidden items-center justify-center gap-2 mt-3 mb-1">
          {sortedPlans.map((plan, idx) => (
            <button
              key={`dot-${plan.planId}`}
              type="button"
              aria-label={`Show ${plan.planName} plan`}
              onClick={() => scrollToCard(idx)}
              className={`h-2 rounded-full transition-all ${
                carouselIdx === idx ? 'w-5 bg-white' : 'w-2 bg-white/40'
              }`}
            />
          ))}
        </div>
        </>
      )}
    </div>
  );
};

export default SubscriptionPlan;
